import React, { useCallback, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import { ADMIN_ROLES } from '../../../constants/roleConstants';
import {
  getDashboardMetricsPreSaleEndpoint,
  getDashboardMetricsV2Endpoint,
  getDashboardPreSaleVisitsProperEndpoint,
  getDashboardVisitsProperEndpoint,
} from '../services/dashboardService';
import useAdminEntity from '../../../hooks/useAdminEntity';
import {
  PeriodMetrics,
  ProperPreSaleMetrics,
  PropersAssignedPreSaleVisits,
  PropersAssignedVisits,
  ProperV2Metrics,
} from '../models/dashboardModels';
import { Col, Container, Row } from 'reactstrap';
import useAdminPagination from '../../../hooks/useAdminPagination';
import AdminPaginator from '../../../components/admin/AdminPaginator';
import DashboardVisitManagerListTable from './DashboardVisitManagerListTable';
import DashboardPreSaleVisitManagerListTable from './DashboardPreSaleVisitManagerListTable';
import { activeTab } from '../types/dashboard.types';
import NavigationTabDashboard from './NavigationTabDashboard';
import DashboardFilterBar from './DashboardFilterBar';
import MetricsDashboard from './MetricsDashboard';
import MetricsDateDashboard from './MetricsDateDashboard';
import useUserCountry from '../../../hooks/useUserCountry';
import {
  toStatsMetrics,
  VISIT_STATUS_MAP,
  PRE_SALE_STATUS_MAP,
} from '../adapters/dashboardAdapters';

const noOpMetricsCall = (period: PeriodMetrics) => ({
  call: () =>
    Promise.resolve({
      status: 200,
      data: { data: { period, visits: [] } },
    }),
  controller: new AbortController(),
});

const noOpListCall = () => ({
  call: () =>
    Promise.resolve({
      status: 200,
      data: { data: { items: [], meta: { page: 1, pageCount: 0 } } },
    }),
  controller: new AbortController(),
});

const InternalUserDashboard: React.FC = () => {
  const { getAuthToken, getUserRoles } = useAuth();
  const token = getAuthToken();
  const { getCurrentCountry } = useUserCountry();
  const currenUserCountryCode = getCurrentCountry()?.code || '';

  const SDR_ROLE_TAB_MAP: Record<string, activeTab> = {
    [ADMIN_ROLES.SDR_RENT]: 'rent',
    [ADMIN_ROLES.SDR_SALES]: 'secondary',
    [ADMIN_ROLES.SDR_PRESALES]: 'pre-sale',
  };

  const userRoles = getUserRoles();
  const matchedSdrRoles = userRoles.filter(
    (role: string) => role in SDR_ROLE_TAB_MAP,
  );
  const restrictedTab: activeTab | null =
    userRoles.length === 1 && matchedSdrRoles.length === 1
      ? SDR_ROLE_TAB_MAP[matchedSdrRoles[0]]
      : null;

  const [activeTab, setActiveTab] = useState<activeTab>(
    restrictedTab ?? (currenUserCountryCode === 'SV' ? 'rent' : 'pre-sale'),
  );

  const [showOnlyAssigned, setShowOnlyAssigned] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('');
  const [selectedSdrId, setSelectedSdrId] = useState('');

  const handleDateRangeChange = (newStart: string, newEnd: string) => {
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleTabChange = (tab: activeTab) => {
    setActiveTab(tab);
    setShowOnlyAssigned(false);
    setStartDate('');
    setEndDate('');
    setStatus('');
    setSelectedSdrId('');
  };

  const handleClearFilters = () => {
    setShowOnlyAssigned(false);
    setStartDate('');
    setEndDate('');
    setStatus('');
    setSelectedSdrId('');
  };

  const defaultPeriod: PeriodMetrics = { end: '', start: '' };

  // Rent metrics
  const getRentMetrics = useCallback(() => {
    if (restrictedTab && restrictedTab !== 'rent') {
      return noOpMetricsCall(defaultPeriod);
    }
    return getDashboardMetricsV2Endpoint(token, 'internal-user', 'on_rent', {
      onlyAssigned: restrictedTab === 'rent' && showOnlyAssigned,
      startDate: activeTab === 'rent' ? startDate || undefined : undefined,
      endDate: activeTab === 'rent' ? endDate || undefined : undefined,
      status: activeTab === 'rent' ? status || undefined : undefined,
      sdrId:
        !restrictedTab && activeTab === 'rent'
          ? selectedSdrId || undefined
          : undefined,
    });
  }, [
    token,
    restrictedTab,
    showOnlyAssigned,
    activeTab,
    startDate,
    endDate,
    status,
    selectedSdrId,
  ]);
  const [rentMetrics] = useAdminEntity<ProperV2Metrics>(
    getRentMetrics,
    console.error,
  );

  // Secondary metrics
  const getSecondaryMetrics = useCallback(() => {
    if (restrictedTab && restrictedTab !== 'secondary') {
      return noOpMetricsCall(defaultPeriod);
    }
    return getDashboardMetricsV2Endpoint(token, 'internal-user', 'on_sale', {
      onlyAssigned: restrictedTab === 'secondary' && showOnlyAssigned,
      startDate:
        activeTab === 'secondary' ? startDate || undefined : undefined,
      endDate: activeTab === 'secondary' ? endDate || undefined : undefined,
      status: activeTab === 'secondary' ? status || undefined : undefined,
      sdrId:
        !restrictedTab && activeTab === 'secondary'
          ? selectedSdrId || undefined
          : undefined,
    });
  }, [
    token,
    restrictedTab,
    showOnlyAssigned,
    activeTab,
    startDate,
    endDate,
    status,
    selectedSdrId,
  ]);
  const [secondaryMetrics] = useAdminEntity<ProperV2Metrics>(
    getSecondaryMetrics,
    console.error,
  );

  // Pre-sale metrics
  const getDashboardPreSaleMetrics = useCallback(() => {
    if (restrictedTab && restrictedTab !== 'pre-sale') {
      return noOpMetricsCall(defaultPeriod);
    }
    return getDashboardMetricsPreSaleEndpoint(token, 'internal-user', {
      onlyAssigned: restrictedTab === 'pre-sale' && showOnlyAssigned,
      startDate: activeTab === 'pre-sale' ? startDate || undefined : undefined,
      endDate: activeTab === 'pre-sale' ? endDate || undefined : undefined,
      status: activeTab === 'pre-sale' ? status || undefined : undefined,
      sdrId:
        !restrictedTab && activeTab === 'pre-sale'
          ? selectedSdrId || undefined
          : undefined,
    });
  }, [
    token,
    restrictedTab,
    showOnlyAssigned,
    activeTab,
    startDate,
    endDate,
    status,
    selectedSdrId,
  ]);
  const [dashboardPreSaleMetrics] = useAdminEntity<ProperPreSaleMetrics>(
    getDashboardPreSaleMetrics,
    console.error,
  );

  // Rent visits
  const [rentVisits, setRentVisits] = useState<PropersAssignedVisits[]>([]);
  const getRentVisits = useCallback(
    (queryParams: string, token: string) => {
      if (restrictedTab && restrictedTab !== 'rent') {
        return noOpListCall();
      }
      const extraParams: string[] = ['marketplace_type=on_rent'];
      if (restrictedTab === 'rent' && showOnlyAssigned) {
        extraParams.push('only_assigned=true');
      }
      if (activeTab === 'rent' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'rent' && endDate)
        extraParams.push(`end_date=${endDate}`);
      if (activeTab === 'rent' && status)
        extraParams.push(`status=${status}`);
      if (!restrictedTab && activeTab === 'rent' && selectedSdrId) {
        extraParams.push(`sdr_id=${selectedSdrId}`);
      }
      return getDashboardVisitsProperEndpoint(
        `${queryParams.replace(/&$/, '')}&${extraParams.join('&')}`,
        token,
      );
    },
    [
      restrictedTab,
      showOnlyAssigned,
      activeTab,
      startDate,
      endDate,
      status,
      selectedSdrId,
    ],
  );
  const { paginator: rentPaginator, loadItems: rentLoadItems } =
    useAdminPagination(getRentVisits, setRentVisits, console.error);

  // Secondary visits
  const [secondaryVisits, setSecondaryVisits] = useState<
    PropersAssignedVisits[]
  >([]);
  const getSecondaryVisits = useCallback(
    (queryParams: string, token: string) => {
      if (restrictedTab && restrictedTab !== 'secondary') {
        return noOpListCall();
      }
      const extraParams: string[] = ['marketplace_type=on_sale'];
      if (restrictedTab === 'secondary' && showOnlyAssigned) {
        extraParams.push('only_assigned=true');
      }
      if (activeTab === 'secondary' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'secondary' && endDate)
        extraParams.push(`end_date=${endDate}`);
      if (activeTab === 'secondary' && status)
        extraParams.push(`status=${status}`);
      if (!restrictedTab && activeTab === 'secondary' && selectedSdrId) {
        extraParams.push(`sdr_id=${selectedSdrId}`);
      }
      return getDashboardVisitsProperEndpoint(
        `${queryParams.replace(/&$/, '')}&${extraParams.join('&')}`,
        token,
      );
    },
    [
      restrictedTab,
      showOnlyAssigned,
      activeTab,
      startDate,
      endDate,
      status,
      selectedSdrId,
    ],
  );
  const { paginator: secondaryPaginator, loadItems: secondaryLoadItems } =
    useAdminPagination(getSecondaryVisits, setSecondaryVisits, console.error);

  // Pre-sale visits
  const [dashboardPreSaleVisits, setDashboardPreSaleVisits] = useState<
    PropersAssignedPreSaleVisits[]
  >([]);
  const getPreSaleVisits = useCallback(
    (queryParams: string, token: string) => {
      if (restrictedTab && restrictedTab !== 'pre-sale') {
        return noOpListCall();
      }
      const extraParams: string[] = [];
      if (restrictedTab === 'pre-sale' && showOnlyAssigned) {
        extraParams.push('only_assigned=true');
      }
      if (activeTab === 'pre-sale' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'pre-sale' && endDate)
        extraParams.push(`end_date=${endDate}`);
      if (activeTab === 'pre-sale' && status)
        extraParams.push(`status=${status}`);
      if (!restrictedTab && activeTab === 'pre-sale' && selectedSdrId) {
        extraParams.push(`sdr_id=${selectedSdrId}`);
      }
      const separator = extraParams.length
        ? `${queryParams.includes('?') ? '&' : '?'}${extraParams.join('&')}`
        : '';
      return getDashboardPreSaleVisitsProperEndpoint(
        `${queryParams}${separator}`,
        token,
      );
    },
    [
      restrictedTab,
      showOnlyAssigned,
      activeTab,
      startDate,
      endDate,
      status,
      selectedSdrId,
    ],
  );
  const {
    paginator: paginatorPreSaleVisits,
    loadItems: dashboardPreSaleVisitLoadItems,
  } = useAdminPagination(
    getPreSaleVisits,
    setDashboardPreSaleVisits,
    console.error,
  );

  if (!rentMetrics || !secondaryMetrics || !dashboardPreSaleMetrics) {
    return <>Cargando</>;
  }

  const rentInfo = toStatsMetrics(rentMetrics.visits, VISIT_STATUS_MAP);
  const secondaryInfo = toStatsMetrics(
    secondaryMetrics.visits,
    VISIT_STATUS_MAP,
  );
  const preSaleInfo = toStatsMetrics(
    dashboardPreSaleMetrics.visits,
    PRE_SALE_STATUS_MAP,
  );

  const activeStats =
    activeTab === 'rent'
      ? rentInfo
      : activeTab === 'secondary'
        ? secondaryInfo
        : preSaleInfo;

  const activePeriod =
    activeTab === 'rent'
      ? (rentMetrics?.period ?? defaultPeriod)
      : activeTab === 'secondary'
        ? (secondaryMetrics?.period ?? defaultPeriod)
        : (dashboardPreSaleMetrics?.period ?? defaultPeriod);

  return (
    <Container className="mt-6" fluid>
      <NavigationTabDashboard
        activeTab={activeTab}
        onTabChange={handleTabChange}
        preSaleCount={preSaleInfo.total}
        rentCount={rentInfo.total}
        secondaryCount={secondaryInfo.total}
        visibleTabs={restrictedTab ? [restrictedTab] : undefined}
      />

      <DashboardFilterBar
        activeTab={activeTab}
        isRestricted={!!restrictedTab}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        status={status}
        onStatusChange={setStatus}
        showOnlyAssigned={showOnlyAssigned}
        onShowOnlyAssignedChange={setShowOnlyAssigned}
        selectedSdrId={selectedSdrId}
        onSdrChange={setSelectedSdrId}
        onClearFilters={handleClearFilters}
      />

      <MetricsDateDashboard activeTab={activeTab} period={activePeriod} />

      <MetricsDashboard activeTab={activeTab} stats={activeStats} />

      {activeTab === 'rent' && (
        <Row>
          <Col sm={12}>
            <DashboardVisitManagerListTable
              visits={rentVisits}
              footer={
                <AdminPaginator
                  page={rentPaginator.page}
                  totalPages={rentPaginator.pageCount}
                  onPageChange={rentLoadItems}
                />
              }
            />
          </Col>
        </Row>
      )}

      {activeTab === 'secondary' && (
        <Row>
          <Col sm={12}>
            <DashboardVisitManagerListTable
              visits={secondaryVisits}
              footer={
                <AdminPaginator
                  page={secondaryPaginator.page}
                  totalPages={secondaryPaginator.pageCount}
                  onPageChange={secondaryLoadItems}
                />
              }
            />
          </Col>
        </Row>
      )}

      {activeTab === 'pre-sale' && (
        <Row>
          <Col sm={12}>
            <DashboardPreSaleVisitManagerListTable
              visits={dashboardPreSaleVisits}
              footer={
                <AdminPaginator
                  page={paginatorPreSaleVisits.page}
                  totalPages={paginatorPreSaleVisits.pageCount}
                  onPageChange={dashboardPreSaleVisitLoadItems}
                />
              }
            />
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default InternalUserDashboard;
